import createMetadataAPITypes from './api-metadata-types';
import { omit, isEqual, compact, uniq } from 'lodash';

const EMPTY_ARRAY = [];

export default ({ baseType, transform, skipByIdUpdate }) => {
  if (!baseType) {
    throw new Error('Basetype is not defined');
  }

  const types = createMetadataAPITypes({ baseType });
  transform = transform || (o => o); // default transform in noop

  const initialPaginationState = {
    page: null,
    perPage: null,
    total: null,
    pagesLoaded: EMPTY_ARRAY
  };

  const initialState = {
    byId: {},
    // keeps track of timestamps of error streak, useful in
    // determining when to abort
    errorTimes: EMPTY_ARRAY,
    error: null,
    lastFetch: null,
    fullFetchComplete: false,
    loading: false,
    paginationStatus: initialPaginationState
  };

  // return an idMap for data, from data.id to the data item. If replace is
  // false, the data will be merged with the old idMap, else it will be replaced
  const idMap = (oldById, data, replace, except = null) => {
    let result = replace ? {} : oldById;

    for (const item of data) {
      if (except && except.includes(item.id)) continue;

      // be frugal with state updates. Only update the state if the new and old
      // objects are not equal
      if (!isEqual(item, result[item.id])) {
        result = {
          ...result,
          [item.id]: item
        };
      }
    }

    return result;
  };

  // clear validation errors for the specified keys
  const clearValidationErrors = (validationErrors, keys) => {
    if (!validationErrors) return {};
    return omit(validationErrors, [...keys, 'base']);
  };

  // here we're returning our customized reducer
  return (state = initialState, action) => {
    let validationErrors = null;
    switch (action.type) {
      case types.START: {
        return {
          ...state,
          loading: true,
          paginationStatus: initialPaginationState
        };
      }
      case types.SUCCESS: {
        let newById = state.byId;
        if (!skipByIdUpdate) {
          // If the data is a single element, convert it into a single item array
          // This is useful for endpoints like pager configuration to make use of
          // this metadata reducer
          const data = [action.payload.data].flat().map(d => transform(d));
          newById = idMap(state.byId, data, action.replace, action.except);
        }
        const { page, perPage, total } = action.payload.loadingStatus;
        const paginationStatus = state.paginationStatus || initialPaginationState;
        const newPagesLoaded = compact(uniq([...paginationStatus.pagesLoaded, Number(page)]));

        // if successful we store our data store the lastFetch timestamp clear
        // out any errors and set loading to false
        const newState = {
          ...state,
          byId: newById,
          error: null,
          errorTimes: EMPTY_ARRAY,
          loading: false,
          fullFetchComplete: state.fullFetchComplete || action.fullFetchComplete,
          paginationStatus: {
            ...state.paginationStatus,
            page: page && Number(page),
            perPage: perPage && Number(perPage),
            total: total && Number(total),
            pagesLoaded: newPagesLoaded
          }
        };

        // if the state is the same as the new state, we don't want to trigger a
        // re-render
        return isEqual(state, newState) ? state : { ...newState, lastFetch: action.timestamp };
      }
      case types.ERROR: {
        // we still want to leave existing data intact as well as "last fetch"
        // which would let us determine if the data is stale or not
        const errorTimes = [...(state.errorTimes || EMPTY_ARRAY), action.timestamp];
        return {
          ...state,
          error: action.error,
          errorTimes,
          loading: false
        };
      }
      case types.SAVE_TO_BACKEND_SUCCESS: {
        return {
          ...state,
          byId: {
            ...state.byId,
            [action.payload.data.id]: transform(action.payload.data)
          }
        };
      }
      case types.CREATE_IN_BACKEND_SUCCESS: {
        // need to do two things: remove the old (temporary) ID, create the new
        // ID
        const newById = {
          ...state.byId,
          [action.payload.data.id]: {
            ...transform(action.payload.data),
            transient: false
          }
        };
        delete newById[action.payload.oldId];

        return {
          ...state,
          byId: newById
        };
      }
      case types.OBJECT_CREATED: {
        // new object created, but not persisted in backend
        return {
          ...state,
          byId: {
            ...state.byId,
            [action.payload.data.id]: action.payload.data
          }
        };
      }
      case types.OBJECTS_CREATED: {
        const newObjects = {};

        for (const obj of action.payload.data) newObjects[obj.id] = obj;

        return {
          ...state,
          byId: {
            ...state.byId,
            ...newObjects
          }
        };
      }
      case types.FIELDS_UPDATED: {
        // field value updated
        const object = state.byId[action.payload.id];
        if (!object) return state;

        validationErrors = clearValidationErrors(object.validationErrors, Object.keys(action.payload.values));
        const newValue = {
          ...object,
          validationErrors,
          ...action.payload.values
        };
        return {
          ...state,
          byId: {
            ...state.byId,
            [action.payload.id]: newValue
          }
        };
      }
      case types.VALIDATION_ERRORS_RECEIVED: {
        validationErrors = action.payload.validationErrors;
        const objectWithErrors = {
          ...state.byId[action.payload.id],
          validationErrors
        };
        return {
          ...state,
          byId: {
            ...state.byId,
            [action.payload.id]: objectWithErrors
          }
        };
      }
      case types.DELETE_FROM_BACKEND_SUCCESS: {
        const id = String(action.payload.id);
        const { [id]: omit, ...byId } = state.byId;
        return { ...state, byId };
      }
      default: {
        return state;
      }
    }
  };
};
